import React from 'react'

import { Box } from '@factorialco/deprecated-design-system'

import Loading from 'modules/core/components/Loading'
import { useForm } from 'modules/core/hooks/useForm'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { employeeLegalEntity } from 'modules/core/lib/employeeLegalEntity'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useUpdateEmployee } from 'modules/employees/hooks/useUpdateEmployee'
import { getDirtyValues } from 'modules/employees/lib/getDirtyValues'
import type Employee from 'modules/employees/store/models/Employee'

import FormSection from '../FormSection'
import ContactSection from './ContactSection'
import { ContactValues } from './ContactValues'

interface Props {
  employee: Employee
  canEdit: boolean
}

const ContactInformation: React.FC<Props> = ({ employee, canEdit }) => {
  const { update } = useUpdateEmployee()

  const form = useForm<ContactValues>({
    defaultValues: {
      phoneNumber: employee.attributes.phone_number ?? '',
      contactName: employee.attributes.contact_name ?? '',
      contactNumber: employee.attributes.contact_number ?? '',
      personalEmail: employee.attributes.personal_email ?? '',
    },
    onSubmit: async (data, { setErrors }) => {
      track(trackingEvents.employee.editEmployeeInformationSubmit, {
        fields_edited: [
          ...getDirtyValues(
            Object.entries(form.values),
            Object.fromEntries(Object.entries(form.initialValues))
          ),
        ],
        section_edited: 'contact_information',
        legal_entity_country: employeeLegalEntity(employee).country,
        layout_version: 'v2',
      })

      const result = await update({
        ...data,
        id: employee.id,
        accessId: employee.attributes.access_id,
      })

      const handled = handleResult(result, (errors) => {
        setErrors({ ...errors })
      })

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })
        const values = form.values
        form.resetForm({ values })
      }

      return handled
    },
  })

  const handleFieldChanged = (fieldName: string, fieldValue: string | undefined | boolean) => {
    form.setValues({ ...form.values, [fieldName]: fieldValue })
  }

  return (
    <FormSection
      title={i18n.t('employee_form.contact_section.title')}
      description={i18n.t('employee_form.contact_section.description')}
      illustration="events-communications"
      disabled={!form.isValid || !form.dirty}
      submitAction={() => form.handleSubmit()}
    >
      {form.isSubmitting && <Loading />}
      {!form.isSubmitting && (
        <Box gap="s24">
          <ContactSection
            values={form.values}
            onChangeValue={(fieldName, fieldValue) => handleFieldChanged(fieldName, fieldValue)}
            readonly={!canEdit}
            employee={employee}
          />
        </Box>
      )}
    </FormSection>
  )
}

export default ContactInformation
